import { MeetingContainerAPI, MeetingSourceElement, MeetingSourceElementRef, MeetingSourceDeviceType, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { MediaCaptureRef } from "@kalyzee/kast-app-web-components";
import { MeetingSourceExtended } from "../../hooks/meeting";
import React, { createRef, useCallback, useEffect, useMemo, useRef } from "react";
import { getAudioContextForVolumeAnalyzer } from "../../helpers/audio";
import { MediaSourcesCarousel, MediaCarouselSource, CircleIcon, PrimaryButton } from "@kalyzee/kalyzee-ui";
import { toastError } from "../../helpers/toast";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useAppStoreDispatch } from "../../contexts/appStore/context";
import clsx from "clsx";

import styles from "./MediaSourcePicker.module.css";

export interface MediaSourcePickerProps {
  meetingAPI?: MeetingContainerAPI;
  /** When true (default when meetingAPI is set), sources without a backend media are greyed out with an error. Set to false for spectators configuring before having backend medias. */
  requireBackendMedia?: boolean;
  display?: boolean;
  sources: MeetingSourceExtended[];
  ignoreGestureError?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

(window as any).tracks = {};

const _MediaSourcePicker: React.FC<MediaSourcePickerProps> = ({
  meetingAPI,
  requireBackendMedia = !!meetingAPI,
  display,
  sources,
  ignoreGestureError,
  style,
  className,
}) => {
  const meetingAPIRef = useRefAutoUpdate(meetingAPI);
  const sourcesRef = useRef<{ [key: string]: React.RefObject<MeetingSourceElementRef> }>({});

  const [session] = useMeetingSelector(meetingAPI, "session");
  const medias = session?.medias;
  const mediasRef = useRefAutoUpdate(medias);

  const dispatch = useAppStoreDispatch();
  const dispatchRef = useRefAutoUpdate(dispatch);

  // Per-source preferences from AppStore (persisted to localStorage)
  const sourcePrefs = useAppStoreSelector(state => state.sourcePreferences);
  const sourcePrefsRef = useRefAutoUpdate(sourcePrefs);

  // Source incremented with user device prefs
  const sourcesWithPrefs = useMemo(() => sources.map(source => {
    const pref = source.name ? sourcePrefsRef.current[source.name] : undefined;
    const sourceWithPref = (pref?.audioDeviceId || pref?.videoDeviceId)
      ? {
          ...source,
          devices: source.devices.map(device => {
            if (device.type !== MeetingSourceDeviceType.WEB) return device;
            return {
              ...device,
              data: {
                ...device.data,
                ...(pref.audioDeviceId ? { defaultAudioDevice: { deviceId: pref.audioDeviceId } } : {}),
                ...(pref.videoDeviceId ? { defaultVideoDevice: { deviceId: pref.videoDeviceId } } : {}),
              },
            };
          }),
        }
      : source;
    return sourceWithPref;
  }), [sources]);

  // Fallback to AppStore global values when no per-source preference exists
  const globalAudio = useAppStoreSelector(state => state.audioStreamEnabled);
  const globalVideo = useAppStoreSelector(state => state.videoStreamEnabled);

  const getSourcePref = useCallback((name: string) => ({
    audioEnabled: sourcePrefs[name]?.audioEnabled ?? globalAudio,
    videoEnabled: sourcePrefs[name]?.videoEnabled ?? globalVideo,
  }), [sourcePrefs, globalAudio, globalVideo]);

  const multipleVisible = useMemo(() => {
    return sources.filter(s => s.visible).length > 1;
  }, [sources]);

  // Resolve per-source enabled state: backend media if connected, config pref if not
  const getSourceState = useCallback((source: MeetingSourceExtended) => {
    const name = source.name!;
    const backendMedia = medias?.find(m => m.name === name);
    const isError = requireBackendMedia && !backendMedia;

    const audioEnabled = backendMedia ? backendMedia.audio.enabled : getSourcePref(name).audioEnabled;
    const videoEnabled = backendMedia ? backendMedia.video.enabled : getSourcePref(name).videoEnabled;

    return { backendMedia, isError, audioEnabled, videoEnabled };
  }, [medias, requireBackendMedia, getSourcePref]);

  // Apply enable/disable to MeetingSourceElement refs
  useEffect(() => {
    sources.forEach((source) => {
      if (!source.name) return;
      const ref = sourcesRef.current[source.name]?.current;
      if (!ref) return;
      const state = getSourceState(source);
      if (state.isError) return;
      ref.enableAudio(state.audioEnabled);
      ref.enableVideo(state.videoEnabled);
    });
  }, [sources, getSourceState]);

  // Sync general AppStore state + persist per-source preferences from resolved states
  useEffect(() => {
    let anyAudio = false;
    let anyVideo = false;
    sources.forEach((source) => {
      if (!source.name) return;
      const state = getSourceState(source);
      if (state.isError) return;
      if (state.audioEnabled) anyAudio = true;
      if (state.videoEnabled) anyVideo = true;
      dispatch("updateSourcePreference", { sourceName: source.name, audioEnabled: state.audioEnabled, videoEnabled: state.videoEnabled });
    });
    dispatch("setAudioStreamEnabled", anyAudio);
    dispatch("setVideoStreamEnabled", anyVideo);
  }, [sources, getSourceState, dispatch]);

  // Toggle handlers (+ refs for stable access in error callbacks)
  const toggleAudioRef = useRef<(source: MeetingSourceExtended) => void>();
  const toggleVideoRef = useRef<(source: MeetingSourceExtended) => void>();

  const toggleAudio = useCallback(async (source: MeetingSourceExtended) => {
    const name = source.name!;
    const state = getSourceState(source);
    if (state.isError) return;

    const newEnabled = !state.audioEnabled;

    if (state.backendMedia && meetingAPI && session) {
      // Connected: update via meeting API (per-media)
      await meetingAPI.updater.medias.update(session.id, state.backendMedia.id, {
        audio: { enabled: newEnabled },
      });
    } else {
      // Disconnected: update ref + save preference
      sourcesRef.current[name]?.current?.enableAudio(newEnabled);
    }
    dispatch("updateSourcePreference", { sourceName: name, audioEnabled: newEnabled });

    // Sync general AppStore state
    const anyAudio = sources.some(s => {
      if (!s.name) return false;
      if (s.name === name) return newEnabled;
      const st = getSourceState(s);
      return st.audioEnabled;
    });
    dispatch("setAudioStreamEnabled", anyAudio);
  }, [getSourceState, meetingAPI, session, sources, dispatch]);

  const toggleVideo = useCallback(async (source: MeetingSourceExtended) => {
    const name = source.name!;
    const state = getSourceState(source);
    if (state.isError) return;

    const newEnabled = !state.videoEnabled;

    if (state.backendMedia && meetingAPI && session) {
      await meetingAPI.updater.medias.update(session.id, state.backendMedia.id, {
        video: { enabled: newEnabled },
      });
    } else {
      sourcesRef.current[name]?.current?.enableVideo(newEnabled);
    }
    dispatch("updateSourcePreference", { sourceName: name, videoEnabled: newEnabled });

    const anyVideo = sources.some(s => {
      if (!s.name) return false;
      if (s.name === name) return newEnabled;
      const st = getSourceState(s);
      return st.videoEnabled;
    });
    dispatch("setVideoStreamEnabled", anyVideo);
  }, [getSourceState, meetingAPI, session, sources, dispatch]);

  toggleAudioRef.current = toggleAudio;
  toggleVideoRef.current = toggleVideo;

  // Stream handling: updateSelfStream always, setMediaStream if connected
  const handleStreamChange = useCallback((sourceName: string, stream: MediaStream) => {
    dispatchRef.current("updateSelfStream", { sourceName, stream });

    const api = meetingAPIRef.current;
    if (!api) return;
    const mediaSession = mediasRef.current?.find((m) => m.name === sourceName);
    if (!mediaSession) return;
    api.setMediaStream(mediaSession.id, stream);
  }, []);

  // When new medias appear (e.g. spectator gets highlighted), attach existing streams
  const attachedMediaIdsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!meetingAPI) return;

    const handler = meetingAPI.emissionManager.addEventListener("create", (ovenSession) => {
      const mediaSession = ovenSession.data.mediaSession;
      if (!mediaSession) return;
      if (attachedMediaIdsRef.current.has(mediaSession.id)) return;

      const mediaName = mediaSession.name;
      if (!mediaName) return;

      const sourceRef = sourcesRef.current[mediaName]?.current;
      const stream = sourceRef?.getData()?.media;
      if (!stream) return;

      attachedMediaIdsRef.current.add(mediaSession.id);
      meetingAPI.setMediaStream(mediaSession.id, stream).then(() => {
        dispatchRef.current("updateSelfStream", { sourceName: mediaName, stream });
      });
    });

    return () => handler.flush();
  }, [meetingAPI]);

  const sourceItems = useMemo(() => {
    return sourcesWithPrefs.map<MediaCarouselSource>((source) => {
      if (!sourcesRef.current[source.name!]) sourcesRef.current[source.name!] = createRef();
      const state = getSourceState(source);

      const content = (
        <div className={clsx(styles.sourceContainer, state.isError && styles.sourceDisabled)}>
          <div className={styles.sourceName}>{source.name}</div>
          <div className={styles.sourceContent}>
            <MeetingSourceElement
              className="meeting-source-expert"
              style={{ height: "100%", width: "100%", border: "unset", backgroundColor: "unset" }}
              ref={sourcesRef.current[source.name!]}
              key={source.name}
              source={source}
              using={!state.isError}
              audioContext={getAudioContextForVolumeAnalyzer()}
              keepAlwaysAudioTrackAliveDefault
              callbacks={{
                track: (_track, data) => {
                  if (data.media && source.name) handleStreamChange(source.name, data.media);
                },
                update: (data) => {
                  if (data.media && source.name) handleStreamChange(source.name, data.media);
                },
                deviceSelected: (deviceInfo) => {
                  if (!deviceInfo || !source.name) return;
                  const { kind, deviceId } = deviceInfo;
                  dispatchRef.current("updateSourcePreference", {
                    sourceName: source.name,
                    ...(kind === "audioinput" ? { audioDeviceId: deviceId } : {}),
                    ...(kind === "videoinput" ? { videoDeviceId: deviceId } : {}),
                  });
                },
                error: (type, _, __, e) => {
                  if (!source.name) return;
                  const isGestureError = e instanceof Error && e.message?.includes("Gesture");
                  if (ignoreGestureError && isGestureError) return;
                  if (type === "audio") {
                    toastError("Veuillez autoriser le navigateur à accéder au microphone");
                    toggleAudioRef.current?.(source);
                  }
                  if (type === "video") {
                    toastError("Veuillez autoriser le navigateur à accéder à la caméra");
                    toggleVideoRef.current?.(source);
                  }
                },
                player: (type, ref, data) => {
                  const currPlayer = data[type].devicePlayer;
                  const currParentPlayer = data[type].parentPlayer;
                  if (ref !== currPlayer && ref !== currParentPlayer) return;

                  // Curr player or parent player was just updated,
                  // We know the videoPlayerRef/audioPlayerRef
                  // inside MeetingSourceElement will be set
                  if (!source.name) return;
                  const state = getSourceState(source);
                  if (state.isError) return;
                  const sourceRef = sourcesRef.current[source.name]?.current;
                  if (!sourceRef) return;

                  if (type === "video") sourceRef.enableVideo(state.videoEnabled);
                  else sourceRef.enableAudio(state.audioEnabled);
                },
              }}
            />
            {state.isError && (
              <div className={styles.sourceError}>Source non disponible</div>
            )}
            {multipleVisible && (
              <div className={styles.sourceControls}>
                <PrimaryButton
                  className={clsx(state.isError && styles.controlBtnDisabled)}
                  onClick={state.isError ? undefined : () => toggleAudio(source)}
                >
                  <CircleIcon.Variants.Mic
                    className={styles.mediaBtn}
                    disabled={!state.audioEnabled}
                    unavailable={state.isError}
                  />
                </PrimaryButton>
                <PrimaryButton
                  className={clsx(state.isError && styles.controlBtnDisabled)}
                  onClick={state.isError ? undefined : () => toggleVideo(source)}
                >
                  <CircleIcon.Variants.Camera
                    className={styles.mediaBtn}
                    disabled={!state.videoEnabled}
                    unavailable={state.isError}
                  />
                </PrimaryButton>
              </div>
            )}
          </div>
        </div>
      );

      return {
        id: `media-source-${source.id}`,
        name: source.name,
        content,
        visible: source.visible,
      };
    });
  }, [sourcesWithPrefs, getSourceState, multipleVisible, toggleAudio, toggleVideo, handleStreamChange, meetingAPI, ignoreGestureError]);

  return (
    <MediaSourcesCarousel
      sources={sourceItems}
      display={display}
      style={style}
      className={clsx(styles.carouselContainer, className)}
    />
  );
}

export const MediaSourcePicker = React.memo(_MediaSourcePicker);

// ---- Global audio/video toggle (for disconnected pages: lobby, list) ----

export interface MediaGlobalControlsProps {
  sources: MeetingSourceExtended[];
  className?: string;
  style?: React.CSSProperties;
}

const _MediaGlobalControls: React.FC<MediaGlobalControlsProps> = ({ sources, className, style }) => {
  const dispatch = useAppStoreDispatch();
  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);

  const toggleAudio = useCallback(() => {
    const newEnabled = !audioEnabled;
    sources.forEach(s => {
      if (s.name) dispatch("updateSourcePreference", { sourceName: s.name, audioEnabled: newEnabled });
    });
    dispatch("setAudioStreamEnabled", newEnabled);
  }, [audioEnabled, sources, dispatch]);

  const toggleVideo = useCallback(() => {
    const newEnabled = !videoEnabled;
    sources.forEach(s => {
      if (s.name) dispatch("updateSourcePreference", { sourceName: s.name, videoEnabled: newEnabled });
    });
    dispatch("setVideoStreamEnabled", newEnabled);
  }, [videoEnabled, sources, dispatch]);

  return (
    <div className={clsx(styles.globalControls, className)} style={style}>
      <PrimaryButton onClick={toggleAudio}>
        <CircleIcon.Variants.Mic disabled={!audioEnabled} />
      </PrimaryButton>
      <PrimaryButton onClick={toggleVideo}>
        <CircleIcon.Variants.Camera disabled={!videoEnabled} />
      </PrimaryButton>
    </div>
  );
};

export const MediaGlobalControls = React.memo(_MediaGlobalControls);
